/* The Andes Austrix · Inicio rediseñado (secciones aa-ix-*). Solo se carga en templates/index.
   Parallax del hero, palabras que rotan, tilt, botones magnéticos, foco de luz, cinta con
   velocidad de scroll, pestañas, galería de oferta, meta de envío gratis con el carrito real,
   carril horizontal fijado y contadores. Respeta prefers-reduced-motion y el editor de temas. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const still = () => reduce.matches || Boolean(window.Shopify?.designMode);
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  // Same format as the shop's money filter (data-money-format = shop.money_format), so JS updates
  // match what Liquid printed. Takes cents.
  const money = (cents, fmt) => {
    const n = Math.round(cents / 100);
    const f = fmt || '${{amount_no_decimals}}';
    const dots = n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const commas = n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => (/comma_separator|space_separator|period_separator/.test(key) ? dots : commas)).replace(/<[^>]*>/g, '');
  };

  // ---- Scroll loop (ticker velocity).
  // Horizon scrolls .page-wrapper at >=990px and the document below that.
  const desktopScroll = window.matchMedia('(min-width: 990px)');
  const wrapper = () => (desktopScroll.matches ? document.querySelector('.page-wrapper') : null);
  const scrollY = () => wrapper()?.scrollTop ?? window.scrollY;
  const onScroll = new Set();
  let ticking = false;
  let lastY = 0;
  const tick = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = scrollY();
      const dy = y - lastY;
      lastY = y;
      onScroll.forEach((fn) => fn(y, dy));
      ticking = false;
    });
  };
  window.addEventListener('scroll', tick, { passive: true });
  const hookWrapper = () => document.querySelector('.page-wrapper')?.addEventListener('scroll', tick, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hookWrapper);
  else hookWrapper();

  // ---- Hero carousel: autoplay with progress dots, arrows, swipe, keyboard; pauses on hover,
  // focus and hidden tab. Reduced motion / editor: no autoplay.
  function initCarousel(root) {
    root.querySelectorAll('[data-aa-ix-carousel]').forEach((sec) => {
      if (sec.dataset.aaIxCar) return;
      sec.dataset.aaIxCar = '1';
      const slides = [...sec.querySelectorAll('[data-aa-ix-slide-i]')];
      const dots = [...sec.querySelectorAll('[data-aa-ix-dot]')];
      const box = sec.querySelector('.aa-ix-carousel');
      if (slides.length < 2 || !box) return;
      const wait = Number(sec.dataset.interval) || 6000;
      box.style.setProperty('--aa-ix-int', `${wait}ms`);
      let cur = 0;
      let timer = 0;
      let paused = false;
      let away = false; // offscreen: no autoplay
      let first = true;
      // ---- One transition for every banner: a plain CSS crossfade (opacity only, see .aa-ix-slide in aa-inicio.css)
      // Load the next banner ahead so the transition never shows an empty frame
      // Banners after the first come with their sources in data-* (see aa-ix-hero.liquid); this turns them on
      const warm = (k) => {
        const media = slides[(k + slides.length) % slides.length].querySelector('.aa-ix-slide__media');
        if (!media) return;
        media.querySelectorAll('source[data-srcset], img[data-srcset]').forEach((el) => {
          el.srcset = el.dataset.srcset;
          el.removeAttribute('data-srcset');
        });
        media.querySelectorAll('img[data-src]').forEach((el) => {
          el.loading = 'eager';
          el.src = el.dataset.src;
          el.removeAttribute('data-src');
        });
      };
      // Floating text resolves from random characters, like the coordinates in the reference video
      const glyphs = '0123456789°′ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const scramble = (el) => {
        const text = el.dataset.text || '';
        clearInterval(el._aaScr);
        let t = 0;
        const total = 22;
        el._aaScr = setInterval(() => {
          t++;
          const done = Math.floor((t / total) * text.length);
          el.textContent = [...text].map((ch, k) => (k < done || ch === ' ' ? ch : glyphs[(k * 7 + t * 13) % glyphs.length])).join('');
          if (t >= total) {
            clearInterval(el._aaScr);
            el.textContent = text;
          }
        }, 38);
      };
      const show = (i) => {
        const prev = cur;
        cur = (i + slides.length) % slides.length;
        // The banner going out keeps its zoom and texts running while it fades under the new one
        if (!first && prev !== cur) {
          const out = slides[prev];
          out.classList.add('is-leaving');
          clearTimeout(out._aaLeave);
          out._aaLeave = setTimeout(() => out.classList.remove('is-leaving'), 900);
        }
        slides[cur].classList.remove('is-leaving');
        // Letters that resolve from random characters: desktop only (Elías, 03/11/2026)
        if (!still() && !matchMedia('(max-width: 749px)').matches) slides[cur].querySelectorAll('[data-aa-ix-scramble]').forEach((el) => setTimeout(() => scramble(el), first ? 500 : 380));
        first = false;
        // The next banner loads only once this one is in, so on slow connections it never competes with the photo on screen
        warm(cur);
        const im = slides[cur].querySelector('.aa-ix-slide__img');
        const k0 = cur;
        if (!im || im.complete) warm(k0 + 1);
        else im.addEventListener('load', () => warm(k0 + 1), { once: true });
        slides.forEach((el, k) => {
          const on = k === cur;
          el.classList.toggle('is-on', on);
          el.toggleAttribute('aria-hidden', !on);
          el.querySelectorAll('a, button').forEach((f) => (f.tabIndex = on && !f.classList.contains('aa-ix-slide__hit') ? 0 : -1));
        });
        dots.forEach((d, k) => {
          d.classList.toggle('is-on', k === cur);
          d.setAttribute('aria-selected', String(k === cur));
        });
        restart();
      };
      const restart = () => {
        clearTimeout(timer);
        box.classList.remove('is-running');
        if (still() || paused || away) return;
        // Wait for the photo on screen: the timer (and its progress dot) start once it has loaded
        const im = slides[cur].querySelector('.aa-ix-slide__img');
        if (im && !im.complete) {
          im.addEventListener('load', restart, { once: true });
          im.addEventListener('error', restart, { once: true });
          return;
        }
        box.classList.add('is-running');
        // Restart the dot progress from zero without forcing a layout (offsetWidth cost a full page layout on load)
        dots[cur]?.querySelector('span')?.getAnimations().forEach((an) => (an.currentTime = 0));
        timer = setTimeout(() => show(cur + 1), wait);
      };
      const pause = (p) => {
        paused = p;
        box.classList.toggle('is-paused', p);
        if (p) {
          clearTimeout(timer);
          box.classList.remove('is-running');
        } else restart();
      };
      sec.querySelectorAll('[data-aa-ix-go]').forEach((b) => b.addEventListener('click', () => show(cur + Number(b.dataset.aaIxGo))));
      dots.forEach((d, k) => d.addEventListener('click', () => show(k)));
      // Mouse parallax: photo and floating text drift in opposite directions
      if (matchMedia('(pointer: fine)').matches && !still()) {
        box.addEventListener('pointermove', (e) => {
          const r = box.getBoundingClientRect();
          box.style.setProperty('--px', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
          box.style.setProperty('--py', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
        });
        box.addEventListener('pointerleave', () => {
          box.style.setProperty('--px', 0);
          box.style.setProperty('--py', 0);
        });
      }
      // Only keyboard focus pauses (WCAG 2.2.2); the mouse never stops the autoplay
      box.addEventListener('focusin', (e) => e.target.matches(':focus-visible') && pause(true));
      box.addEventListener('focusout', () => pause(false));
      box.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') show(cur + 1);
        if (e.key === 'ArrowLeft') show(cur - 1);
      });
      document.addEventListener('visibilitychange', () => (document.hidden ? clearTimeout(timer) : restart()));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => {
          if (away === !e.isIntersecting) return;
          away = !e.isIntersecting;
          if (!away) return restart();
          clearTimeout(timer);
          box.classList.remove('is-running');
        }).observe(box);
      }
      // Swipe
      let x0 = null;
      let y0 = 0;
      box.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        x0 = e.clientX;
        y0 = e.clientY;
      });
      box.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(cur + (dx < 0 ? 1 : -1));
      });
      box.addEventListener('pointercancel', () => (x0 = null));
      show(0);
    });
  }

  // ---- Mouse drag-to-scroll for horizontal rows; a drag never turns into a click
  function initDrag(root) {
    if (!fine.matches) return;
    root.querySelectorAll('[data-aa-ix-drag]').forEach((row) => {
      if (row.dataset.aaIxDragOk) return;
      row.dataset.aaIxDragOk = '1';
      let x = 0;
      let left = 0;
      let moved = false;
      let down = false;
      row.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || row.scrollWidth <= row.clientWidth) return;
        down = true;
        moved = false;
        x = e.clientX;
        left = row.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - x;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          row.classList.add('is-dragging');
        }
        if (moved) row.scrollLeft = left - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        requestAnimationFrame(() => row.classList.remove('is-dragging'));
      });
      row.addEventListener('click', (e) => {
        if (!moved) return;
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }, true);
      row.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  // ---- Prev/next buttons for product rails; hidden at the ends
  function initRails(root) {
    root.querySelectorAll('.aa-ix-railwrap').forEach((wrap) => {
      if (wrap.dataset.aaIxRails) return;
      wrap.dataset.aaIxRails = '1';
      const rail = wrap.querySelector('[data-aa-ix-rail]');
      if (!rail) return;
      const ends = () => {
        const max = rail.scrollWidth - rail.clientWidth - 2;
        wrap.classList.toggle('is-start', rail.scrollLeft <= 2);
        wrap.classList.toggle('is-end', rail.scrollLeft >= max);
        wrap.classList.toggle('is-static', max <= 0);
      };
      wrap.querySelectorAll('[data-aa-ix-rail-nav]').forEach((b) =>
        b.addEventListener('click', () => rail.scrollBy({ left: Number(b.dataset.aaIxRailNav) * rail.clientWidth * 0.85, behavior: 'smooth' }))
      );
      rail.addEventListener('scroll', ends, { passive: true });
      new ResizeObserver(ends).observe(rail);
      ends();
    });
  }

  // ---- 3D tilt following the pointer
  function initTilt(root) {
    if (!fine.matches || still()) return;
    root.querySelectorAll('[data-aa-ix-tilt]').forEach((el) => {
      if (el.dataset.aaIxTilt) return;
      el.dataset.aaIxTilt = '1';
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--ry', `${(x * 10).toFixed(2)}deg`);
        el.style.setProperty('--rx', `${(-y * 10).toFixed(2)}deg`);
      });
      el.addEventListener('pointerleave', () => {
        el.style.setProperty('--ry', '0deg');
        el.style.setProperty('--rx', '0deg');
      });
    });
  }

  // ---- Magnetic buttons
  function initMagnet(root) {
    if (!fine.matches || still()) return;
    root.querySelectorAll('[data-aa-ix-magnet]').forEach((el) => {
      if (el.dataset.aaIxMag) return;
      el.dataset.aaIxMag = '1';
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left - r.width / 2) * 0.22).toFixed(1)}px`);
        el.style.setProperty('--my', `${((e.clientY - r.top - r.height / 2) * 0.32).toFixed(1)}px`);
      });
      el.addEventListener('pointerleave', () => {
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      });
    });
  }

  // ---- Spotlight that follows the cursor (tiles, promise cards)
  function initSpot(root) {
    if (!fine.matches) return;
    root.querySelectorAll('[data-aa-ix-spot]').forEach((el) => {
      if (el.dataset.aaIxSpot) return;
      el.dataset.aaIxSpot = '1';
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--sx', `${e.clientX - r.left}px`);
        el.style.setProperty('--sy', `${e.clientY - r.top}px`);
      });
    });
  }

  // ---- Ticker skews and speeds up with scroll velocity
  function initTicker(root) {
    const tickers = [...root.querySelectorAll('[data-aa-ix-ticker]')];
    if (!tickers.length || still()) return;
    let v = 0;
    let raf = 0;
    const settle = () => {
      v *= 0.9;
      tickers.forEach((t) => t.style.setProperty('--v', v.toFixed(3)));
      raf = Math.abs(v) > 0.01 ? requestAnimationFrame(settle) : 0;
    };
    onScroll.add((y, dy) => {
      v = clamp(v + dy / 120, -1, 1);
      if (!raf) raf = requestAnimationFrame(settle);
    });
  }

  // ---- Tabs with sliding pill
  function initTabs(root) {
    root.querySelectorAll('[data-aa-ix-tabs]').forEach((sec) => {
      if (sec.dataset.aaIxTabs) return;
      sec.dataset.aaIxTabs = '1';
      const tabs = [...sec.querySelectorAll('[data-aa-ix-tab]')];
      const pill = sec.querySelector('.aa-ix-tabs__pill');
      const place = () => {
        const on = tabs.find((t) => t.classList.contains('is-on')) || tabs[0];
        if (!on || !pill) return;
        pill.style.width = `${on.offsetWidth}px`;
        pill.style.transform = `translateX(${on.offsetLeft}px)`;
      };
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (!panel) return;
          panel.hidden = !on;
          panel.classList.toggle('is-on', on);
          if (on) panel.querySelectorAll('.aa-rv').forEach((el) => el.classList.add('is-in'));
        });
        if (focus) tab.focus();
        place();
      };
      tabs.forEach((t, i) => {
        t.tabIndex = t.classList.contains('is-on') ? 0 : -1;
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          const k = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!k) return;
          e.preventDefault();
          select(tabs[(i + k + tabs.length) % tabs.length], true);
        });
      });
      place();
      new ResizeObserver(place).observe(sec);
      document.fonts?.ready.then(place);
    });
  }

  // ---- Deal gallery thumbnails
  function initDeal(root) {
    root.querySelectorAll('[data-aa-ix-deal]').forEach((sec) => {
      if (sec.dataset.aaIxDeal) return;
      sec.dataset.aaIxDeal = '1';
      const slides = [...sec.querySelectorAll('[data-aa-ix-slide]')];
      const thumbs = [...sec.querySelectorAll('[data-aa-ix-thumb]')];
      let cur = 0;
      let timer = 0;
      const show = (i) => {
        cur = i;
        slides.forEach((s, k) => s.classList.toggle('is-on', k === i));
        thumbs.forEach((t, k) => t.classList.toggle('is-on', k === i));
      };
      thumbs.forEach((t, k) =>
        t.addEventListener('click', () => {
          clearInterval(timer);
          show(k);
        })
      );
      if (slides.length > 1 && !still()) {
        const io = new IntersectionObserver(([e]) => {
          clearInterval(timer);
          if (e.isIntersecting) timer = setInterval(() => show((cur + 1) % slides.length), 3800);
        });
        io.observe(sec);
      }
    });
  }

  // ---- Count-up when visible (data-aa-ix-count = integer, data-aa-ix-money = CLP)
  function initCounts(root) {
    const els = [...root.querySelectorAll('[data-aa-ix-count], [data-aa-ix-money]')].filter((el) => !el.dataset.aaIxCounted);
    if (!els.length) return;
    const run = (el) => {
      el.dataset.aaIxCounted = '1';
      const isMoney = el.hasAttribute('data-aa-ix-money');
      const target = Number(isMoney ? el.dataset.aaIxMoney : el.dataset.aaIxCount);
      const fmt = (n) => (isMoney ? money(n * 100, el.dataset.moneyFormat) : Math.round(n).toLocaleString('es-CL'));
      if (!target || still()) {
        el.textContent = fmt(target);
        return;
      }
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min((now - t0) / 1400, 1);
        el.textContent = fmt(target * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if (!('IntersectionObserver' in window)) return els.forEach(run);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          run(e.target);
        });
      },
      { threshold: 0.6 }
    );
    els.forEach((el) => io.observe(el));
  }

  // ---- Free-shipping goal with the real cart
  const ships = new Set();
  function paintShip(sec, total) {
    const goal = Number(sec.dataset.threshold) || 0;
    if (!goal) return;
    const pct = clamp((total / goal) * 100, 0, 100);
    const done = total >= goal;
    sec.style.setProperty('--pct', pct.toFixed(1));
    sec.classList.toggle('is-done', done);
    const left = sec.querySelector('[data-aa-ix-ship-left]');
    if (left) left.textContent = money(Math.max(goal - total, 0), sec.dataset.moneyFormat).replace(/,/g, '.');
    sec.querySelector('[data-aa-ix-ship-msg="todo"]')?.toggleAttribute('hidden', done);
    sec.querySelector('[data-aa-ix-ship-msg="ok"]')?.toggleAttribute('hidden', !done);
    const trail = sec.querySelector('[data-aa-ix-trail]');
    trail?.setAttribute('aria-valuenow', String(Math.round(pct)));
    const path = trail?.querySelector('.aa-ix-trail__done');
    const flag = trail?.querySelector('.aa-ix-trail__flag');
    if (path && flag && path.getTotalLength) {
      const svg = path.ownerSVGElement;
      const len = path.getTotalLength();
      const pt = path.getPointAtLength((len * Math.max(pct, 0.5)) / 100);
      const vb = svg.viewBox.baseVal;
      flag.style.left = `${(pt.x / vb.width) * 100}%`;
      flag.style.top = `${(pt.y / vb.height) * 100}%`;
    }
  }
  async function refreshShip() {
    if (!ships.size) return;
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      // Only the section's own products (Envío Internacional) count toward the goal.
      ships.forEach((sec) => {
        const ids = sec.dataset.ids || '';
        const total = (cart.items || []).reduce((sum, it) => (ids.includes(`,${it.product_id},`) ? sum + (Number(it.final_line_price) || 0) : sum), 0);
        paintShip(sec, total);
      });
    } catch (error) {
      /* keep the server-rendered value */
    }
  }
  function initShip(root) {
    root.querySelectorAll('[data-aa-ix-ship]').forEach((sec) => {
      if (sec.dataset.aaIxShip) return;
      sec.dataset.aaIxShip = '1';
      ships.add(sec);
      const io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        paintShip(sec, Number(sec.dataset.total) || 0);
        refreshShip();
      });
      io.observe(sec);
    });
  }
  // Any cart change on the page (quick add, drawer, product forms) refreshes the goal.
  if (!window.aaIxFetchHook) {
    window.aaIxFetchHook = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = (...args) => {
      const res = nativeFetch(...args);
      const url = String(args[0] instanceof Request ? args[0].url : args[0] || '');
      if (/\/cart\/(add|change|update|clear)/.test(url)) res.then(() => setTimeout(refreshShip, 250)).catch(() => {});
      return res;
    };
    window.addEventListener('pageshow', refreshShip);
  }

  function initChat(root) {
    root.querySelectorAll('[data-aa-ix-chat]').forEach((b) => {
      if (b.dataset.aaIxChat) return;
      b.dataset.aaIxChat = '1';
      b.addEventListener('click', () => document.querySelector('[data-aa-chat-open]')?.click());
    });
  }

  // ---- "Explora Chile" map: route from the base point, floating ticket, guided tour and synced destination cards
  function initMap(root) {
    root.querySelectorAll('[data-aa-ix-map]').forEach((sec) => {
      if (sec.dataset.aaIxMapOk) return;
      sec.dataset.aaIxMapOk = '1';
      const stage = sec.querySelector('[data-aa-ix-stage]');
      const pts = [...sec.querySelectorAll('[data-aa-ix-pt]')];
      const cards = [...sec.querySelectorAll('[data-aa-ix-dcard]')];
      const rail = sec.querySelector('[data-aa-ix-rail]');
      const base = sec.querySelector('[data-aa-ix-base]');
      const svg = sec.querySelector('.aa-ix-map__fx');
      const route = sec.querySelector('[data-aa-ix-route]');
      const runner = sec.querySelector('[data-aa-ix-runner]');
      const tip = sec.querySelector('[data-aa-ix-tip]');
      if (!stage || !pts.length || !base || !route || !tip) return;
      const q = (k) => tip.querySelector(`[data-aa-ix-tip-${k}]`);
      const tipC = q('c');
      const tipN = q('n');
      const tipR = q('r');
      const tipK = q('k');
      const tipT = q('t');
      const bLat = Number(sec.dataset.baseLat);
      const bLon = Number(sec.dataset.baseLon);
      const baseName = base.textContent.trim() || 'Santiago';
      const fmt = new Intl.NumberFormat('es-CL');
      const rad = Math.PI / 180;
      const km = (p) => {
        const la = Number(p.dataset.lat);
        const lo = Number(p.dataset.lon);
        const a = Math.sin(((la - bLat) * rad) / 2) ** 2 + Math.cos(la * rad) * Math.cos(bLat * rad) * Math.sin(((lo - bLon) * rad) / 2) ** 2;
        return fmt.format(Math.round(12742 * Math.asin(Math.sqrt(a))));
      };
      const pt = (i) => pts.find((p) => p.dataset.aaIxPt === String(i));
      const card = (i) => cards.find((c) => c.dataset.aaIxDcard === String(i));
      cards.forEach((c) => {
        const p = pt(c.dataset.aaIxDcard);
        const k = c.querySelector('[data-aa-ix-km]');
        if (p && k) k.textContent = `A ${km(p)} km de ${baseName}`;
      });
      // North to south, Antarctica last
      const order = pts.map((p) => p.dataset.aaIxPt).sort((a, b) => Number(pt(b).dataset.lat) - Number(pt(a).dataset.lat));
      const center = (el) => {
        const s = stage.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        return [r.left + r.width / 2 - s.left, r.top + r.height / 2 - s.top];
      };
      const glyphs = '0123456789°′NSWE';
      let sTimer = 0;
      const scramble = (el, text) => {
        clearInterval(sTimer);
        if (still()) {
          el.textContent = text;
          return;
        }
        let f = 0;
        sTimer = setInterval(() => {
          f += 1;
          const n = Math.floor(f / 2);
          el.textContent = [...text].map((ch, j) => (j < n || ch === ' ' ? ch : glyphs[Math.floor(Math.random() * glyphs.length)])).join('');
          if (n >= text.length) clearInterval(sTimer);
        }, 26);
      };
      let raf = 0;
      const draw = (p, instant) => {
        const s = stage.getBoundingClientRect();
        svg.setAttribute('viewBox', `0 0 ${s.width.toFixed(1)} ${s.height.toFixed(1)}`);
        const [x1, y1] = center(base.querySelector('i') || base);
        const [x2, y2] = center(p.querySelector('.aa-ix-map__dot') || p);
        const dx = x2 - x1;
        const dy = y2 - y1;
        const d = Math.hypot(dx, dy) || 1;
        const bend = Math.min(150, d * 0.3);
        // Bulge towards the east side (top) on the horizontal map, the Pacific (left) on the vertical one
        let nx = -dy / d;
        let ny = dx / d;
        if ((s.height > s.width ? nx : ny) > 0) {
          nx = -nx;
          ny = -ny;
        }
        const mx = (x1 + x2) / 2 + nx * bend;
        const my = (y1 + y2) / 2 + ny * bend;
        route.setAttribute('d', `M${x1.toFixed(1)} ${y1.toFixed(1)}Q${mx.toFixed(1)} ${my.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`);
        cancelAnimationFrame(raf);
        route.classList.remove('is-flow');
        if (instant || still()) {
          route.style.strokeDasharray = '';
          route.classList.add('is-flow');
          if (runner) runner.style.opacity = '0';
          return;
        }
        const L = route.getTotalLength();
        const t0 = performance.now();
        const step = (now) => {
          const k = Math.min(1, (now - t0) / 950);
          const e = 1 - (1 - k) ** 3;
          route.style.strokeDasharray = `${(L * e).toFixed(1)} ${L.toFixed(1)}`;
          if (runner) {
            const h = route.getPointAtLength(L * e);
            runner.setAttribute('cx', h.x.toFixed(1));
            runner.setAttribute('cy', h.y.toFixed(1));
            runner.style.opacity = k < 1 ? '1' : '0';
          }
          if (k < 1) raf = requestAnimationFrame(step);
          else {
            route.style.strokeDasharray = '';
            route.classList.add('is-flow');
          }
        };
        raf = requestAnimationFrame(step);
      };
      const place = (p) => {
        if (getComputedStyle(tip).position !== 'absolute') return;
        const W = stage.clientWidth;
        const H = stage.clientHeight;
        const [x, y] = center(p.querySelector('.aa-ix-map__dot') || p);
        const w = tip.offsetWidth;
        const h = tip.offsetHeight;
        const above = p.classList.contains('aa-ix-map__pt--b');
        // Opposite side to the point's label; beside the point when that side has no room or it sits in an inset
        let top = above ? y - h - 26 : y + 26;
        let left = x - w / 2;
        if (top < 10 || top + h > H - 10 || p.closest('.aa-ix-map__inset')) {
          top = p.closest('.aa-ix-map__inset') ? y - h * 0.8 : y - h / 2;
          const gap = Math.max(26, (p.querySelector('.aa-ix-map__lbl')?.offsetWidth || 0) / 2 + 12);
          left = x < W / 2 ? x + gap : x - w - gap;
        }
        top = Math.max(10, Math.min(H - h - 10, top));
        left = Math.max(12, Math.min(W - w - 12, left));
        tip.style.setProperty('--tx', `${left.toFixed(0)}px`);
        tip.style.setProperty('--ty', `${top.toFixed(0)}px`);
      };
      let cur = null;
      const set = (i, user) => {
        const p = pt(i);
        if (!p) return;
        const c = card(i);
        cur = String(i);
        pts.forEach((o) => {
          o.classList.toggle('is-on', o === p);
          o.setAttribute('aria-pressed', o === p ? 'true' : 'false');
        });
        cards.forEach((o) => o.classList.toggle('is-on', o === c));
        if (c) {
          tipN.textContent = c.dataset.name;
          tipR.textContent = c.dataset.region;
          tipK.textContent = `${km(p)} km desde ${baseName}`;
          if (tipT) tipT.replaceChildren(...[...c.querySelectorAll('[data-aa-ix-trip]')].map((n) => n.cloneNode(true)));
          scramble(tipC, c.dataset.coords);
        }
        place(p);
        sec.classList.add('is-ready');
        draw(p);
        if (user && c && rail) {
          const r = rail.getBoundingClientRect();
          const cr = c.getBoundingClientRect();
          if (cr.left < r.left || cr.right > r.right) rail.scrollBy({ left: cr.left - r.left - 4, behavior: 'smooth' });
        }
      };
      let tour = 0;
      let stopped = false;
      let seen = false;
      let visible = false;
      let hover = false;
      const stopTour = () => {
        clearInterval(tour);
        tour = 0;
      };
      const startTour = () => {
        if (stopped || still() || tour || !visible || hover) return;
        tour = setInterval(() => set(order[(order.indexOf(cur) + 1) % order.length]), 4200);
      };
      const pick = (i) => {
        stopped = true;
        stopTour();
        set(i, true);
      };
      pts.forEach((p) => p.addEventListener('click', () => pick(p.dataset.aaIxPt)));
      cards.forEach((c) => {
        c.addEventListener('click', (e) => {
          if (e.target.closest('a')) return;
          pick(c.dataset.aaIxDcard);
          if (e.target.closest('[data-aa-ix-locate]')) {
            const r = stage.getBoundingClientRect();
            if (r.bottom < 60 || r.top > window.innerHeight - 60) stage.scrollIntoView({ block: 'center', behavior: still() ? 'auto' : 'smooth' });
          }
        });
      });
      if (fine.matches) {
        stage.addEventListener('pointerenter', () => {
          hover = true;
          stopTour();
        });
        stage.addEventListener('pointerleave', () => {
          hover = false;
          startTour();
        });
      }
      new IntersectionObserver(
        ([e]) => {
          visible = e.isIntersecting;
          if (!visible) return stopTour();
          sec.classList.add('is-in');
          if (!seen) {
            seen = true;
            setTimeout(() => set(cur ?? order[0]), still() ? 0 : 1300);
          }
          startTour();
        },
        { threshold: 0.3 }
      ).observe(stage);
      new ResizeObserver(() => {
        const p = cur !== null && pt(cur);
        if (!p) return;
        place(p);
        draw(p, true);
      }).observe(stage);
    });
  }

  // ---- Infinite CSS loops (waves, pulses, shines, rotating borders, marquee) pause while their section is off screen
  let pauseIo = null;
  function initPause(root) {
    if (!('IntersectionObserver' in window)) return;
    pauseIo = pauseIo || new IntersectionObserver((list) => list.forEach((e) => e.target.classList.toggle('aa-ix-off', !e.isIntersecting)), { rootMargin: '120px 0px' });
    root.querySelectorAll('.aa-ix-sec, .aa-bar, [data-aa-ix-pausable]').forEach((el) => {
      if (el.dataset.aaIxPause) return;
      el.dataset.aaIxPause = '1';
      pauseIo.observe(el);
    });
  }

  // ---- Diario: zone accordion (opens on hover, focus or tap) and destination chips that jump to the map card
  function initZones(root) {
    root.querySelectorAll('[data-aa-dx-zones]').forEach((sec) => {
      if (sec.dataset.aaDxOk) return;
      sec.dataset.aaDxOk = '1';
      const zones = [...sec.querySelectorAll('[data-aa-dx-zone]')];
      const wide = matchMedia('(min-width: 990px)');
      // Named openZone, not open: a bare open() would be window.open and navigate to "[object HTMLElement]"
      const openZone = (z) => {
        if (!wide.matches || z.classList.contains('is-open')) return;
        zones.forEach((o) => o.classList.toggle('is-open', o === z));
      };
      zones.forEach((z) => {
        z.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && openZone(z));
        z.addEventListener('focusin', () => openZone(z));
        z.addEventListener('click', () => openZone(z));
        z.addEventListener('keydown', (e) => {
          if (e.target !== z) return;
          const k = zones.indexOf(z) + (e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0);
          if (k !== zones.indexOf(z) && zones[k]) {
            e.preventDefault();
            zones[k].focus();
          }
        });
      });
      sec.querySelectorAll('[data-aa-dx-go]').forEach((a) =>
        a.addEventListener('click', (e) => {
          const card = [...document.querySelectorAll('[data-aa-ix-dcard]')].find((c) => c.dataset.name === a.dataset.aaDxGo);
          const btn = card && card.querySelector('[data-aa-ix-locate]');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          btn.click(); // the map picks the place and syncs its card rail
          // Coming from the zones, always bring the whole map into view (its own check only fires when it is off-screen)
          const stage = card.closest('[data-aa-ix-map]')?.querySelector('[data-aa-ix-stage]') || document.querySelector('[data-aa-ix-stage]');
          if (stage) stage.scrollIntoView({ block: window.innerHeight < stage.offsetHeight + 80 ? 'start' : 'center', behavior: still() ? 'auto' : 'smooth' });
        })
      );
    });
  }

  // Below-the-fold photos rendered as data-src (data-aa-later): they wait for the cover photo (LCP) so they do not
  // share its bandwidth, unless the visitor scrolls them well into view first.
  function initLater(root) {
    const imgs = [...root.querySelectorAll('img[data-aa-later]')];
    if (!imgs.length) return;
    const show = (im) => {
      if (!im.hasAttribute('data-aa-later')) return;
      im.removeAttribute('data-aa-later');
      if (im.dataset.srcset) im.srcset = im.dataset.srcset;
      if (im.dataset.src) im.src = im.dataset.src;
      im.removeAttribute('data-srcset');
      im.removeAttribute('data-src');
    };
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach((x) => x.isIntersecting && (io.unobserve(x.target), show(x.target))), { rootMargin: '0px 0px -20% 0px' }) : null;
    const all = () => {
      io && io.disconnect();
      imgs.forEach(show);
    };
    const cover = document.querySelector('img[fetchpriority="high"]');
    if (!io || !cover || (cover.complete && cover.currentSrc)) return all();
    imgs.forEach((im) => io.observe(im));
    cover.addEventListener('load', all, { once: true });
    cover.addEventListener('error', all, { once: true });
    setTimeout(all, 12000);
  }

  function init(root = document) {
    initLater(root);
    initCarousel(root);
    initDrag(root);
    initRails(root);
    initTilt(root);
    initMagnet(root);
    initSpot(root);
    initTicker(root);
    initTabs(root);
    initDeal(root);
    initCounts(root);
    initShip(root);
    initChat(root);
    initMap(root);
    initZones(root);
    initPause(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
